/**
 * AgenticExtensions.js
 * =====================
 * Main page for all Agentic Extension features.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  DocumentComposerPanel,
  ContractDiffPanel,
  DecisionTimelinePanel,
  IndustryPackSelector,
  BulkIntelligencePanel,
  MemoryIntelligencePanel,
  IntegrationHubPanel
} from '../components/agenticExtensions';
import MeetingPrepPanel from '../components/agenticExtensions/MeetingPrepPanel';
import { 
  Sparkles, FileEdit, GitCompare, Layers, Package, 
  Clock, Database, Plug, RefreshCw, Loader2, CheckCircle2,
  AlertTriangle, Zap, Presentation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgenticExtensions = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStatus(),
        fetchDocuments()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStatus(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/documents/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both array response and object with documents key
        const docs = Array.isArray(data) ? data : (data.documents || data || []);
        setDocuments(docs);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments([]);
    }
  };

  const enabledFeatures = status?.features ? 
    Object.entries(status.features).filter(([, enabled]) => enabled).length : 0;
  const totalFeatures = status?.features ? Object.keys(status.features).length : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Agentic Extensions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agentic-extensions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Agentic Extensions
          </h1>
          <p className="text-muted-foreground mt-1">
            Enterprise AI-powered document intelligence features
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="py-1.5">
            <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
            {enabledFeatures}/{totalFeatures} Features Active
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchInitialData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Feature Status Cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { key: 'document_composer', label: 'Document Composer', icon: FileEdit, color: 'text-blue-600 bg-blue-50' },
          { key: 'contract_diff', label: 'Contract Diff', icon: GitCompare, color: 'text-purple-600 bg-purple-50' },
          { key: 'bulk_intelligence', label: 'Bulk Intelligence', icon: Layers, color: 'text-green-600 bg-green-50' },
          { key: 'memory_intelligence', label: 'Memory Intelligence', icon: Database, color: 'text-orange-600 bg-orange-50' },
        ].map(feature => {
          const Icon = feature.icon;
          const isEnabled = status?.features?.[feature.key];
          return (
            <Card key={feature.key} className={`${isEnabled ? '' : 'opacity-60'}`}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${feature.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{feature.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                  {isEnabled ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="composer" className="space-y-4">
        <TabsList className="grid grid-cols-8 h-auto p-1">
          <TabsTrigger value="composer" className="flex items-center gap-1.5 py-2" data-testid="tab-composer">
            <FileEdit className="h-4 w-4" />
            <span className="hidden sm:inline">Composer</span>
          </TabsTrigger>
          <TabsTrigger value="diff" className="flex items-center gap-1.5 py-2" data-testid="tab-diff">
            <GitCompare className="h-4 w-4" />
            <span className="hidden sm:inline">Diff</span>
          </TabsTrigger>
          <TabsTrigger value="bulk" className="flex items-center gap-1.5 py-2" data-testid="tab-bulk">
            <Layers className="h-4 w-4" />
            <span className="hidden sm:inline">Bulk</span>
          </TabsTrigger>
          <TabsTrigger value="packs" className="flex items-center gap-1.5 py-2" data-testid="tab-packs">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">Packs</span>
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-1.5 py-2" data-testid="tab-timeline">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Timeline</span>
          </TabsTrigger>
          <TabsTrigger value="memory" className="flex items-center gap-1.5 py-2" data-testid="tab-memory">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Memory</span>
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-1.5 py-2" data-testid="tab-integrations">
            <Plug className="h-4 w-4" />
            <span className="hidden sm:inline">Integrations</span>
          </TabsTrigger>
          <TabsTrigger value="meeting-prep" className="flex items-center gap-1.5 py-2" data-testid="tab-meeting-prep">
            <Presentation className="h-4 w-4" />
            <span className="hidden sm:inline">Meeting Prep</span>
          </TabsTrigger>
        </TabsList>

        {/* Document Composer Tab */}
        <TabsContent value="composer">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DocumentComposerPanel />
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  Quick Start Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-primary">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">Select Document Type</p>
                      <p className="text-xs text-muted-foreground">Choose from RFP, Contract, SOP, Letter, Policy, etc.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-primary">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">Add Context</p>
                      <p className="text-xs text-muted-foreground">Describe your requirements and specifications</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-primary">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">Generate & Review</p>
                      <p className="text-xs text-muted-foreground">AI creates a professional draft for your review</p>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-sm mb-2">Supported Types</h4>
                  <div className="flex flex-wrap gap-2">
                    {['RFP', 'Contract', 'SOP', 'Letter', 'Policy', 'Memo', 'Report', 'Proposal'].map(type => (
                      <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contract Diff Tab */}
        <TabsContent value="diff">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ContractDiffPanel documents={documents} />
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Comparison Modes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-medium">Semantic Analysis</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      AI understands the meaning and intent of clauses, identifying conceptual differences even when wording varies.
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-medium">Clause-by-Clause</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Structured comparison of individual clauses with risk assessment for each change.
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <h4 className="font-medium">Full Document Diff</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Complete text comparison showing all additions, deletions, and modifications.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Bulk Intelligence Tab */}
        <TabsContent value="bulk">
          <BulkIntelligencePanel 
            documents={documents} 
            onProcessComplete={fetchDocuments}
          />
        </TabsContent>

        {/* Industry Packs Tab */}
        <TabsContent value="packs">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <IndustryPackSelector 
                documentId={selectedDoc}
                onPackApplied={() => {
                  toast.success('Industry pack applied!');
                  fetchDocuments();
                }}
              />
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Document</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {documents.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No documents available
                    </p>
                  ) : (
                    documents.map(doc => (
                      <button
                        key={doc.id}
                        onClick={() => setSelectedDoc(doc.id)}
                        className={`w-full p-2 text-left rounded-lg border transition-colors ${
                          selectedDoc === doc.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-transparent hover:border-border hover:bg-muted/30'
                        }`}
                      >
                        <div className="font-medium text-sm truncate">{doc.title}</div>
                        <div className="text-xs text-muted-foreground">{doc.category || 'Document'}</div>
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Decision Timeline Tab */}
        <TabsContent value="timeline">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <DecisionTimelinePanel documentId={selectedDoc} />
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Document</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {documents.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No documents available
                    </p>
                  ) : (
                    documents.map(doc => (
                      <button
                        key={doc.id}
                        onClick={() => setSelectedDoc(doc.id)}
                        className={`w-full p-2 text-left rounded-lg border transition-colors ${
                          selectedDoc === doc.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-transparent hover:border-border hover:bg-muted/30'
                        }`}
                      >
                        <div className="font-medium text-sm truncate">{doc.title}</div>
                        <div className="text-xs text-muted-foreground">{doc.category || 'Document'}</div>
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Memory Intelligence Tab */}
        <TabsContent value="memory">
          <MemoryIntelligencePanel />
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <IntegrationHubPanel />
        </TabsContent>

        <TabsContent value="meeting-prep">
          <MeetingPrepPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AgenticExtensions;
